import React, { useState, useContext, useEffect, useLayoutEffect } from 'react';
import { Platform, ScrollView, StyleSheet, View } from 'react-native';
import { Button, Dialog, Divider, Portal, Text, Title } from 'react-native-paper';
import { HeaderBackButton } from '@react-navigation/elements';

import { LoginRedirect } from '~utils/loginRedirect';
import { AppContext } from '~context/appContext';
import { V1Request } from '~utils/apiRequest';
import { ShowQuestions } from '~utils/formLogic';

import { Review } from '~components/formInputs/review';
import BrandBackground from '~components/brandBackground';
import { WebContainer } from '~components/webContainer';

export const FormReview = ({ route, navigation }) => {
	const [questions, setQuestions] = useState([]);
	const [buttonText, setButtonText] = useState('');
	const [form, setForm] = useState({});
	const [elementVisibility, setElementVisibility] = useState(false);
	const [dialogOpen, setDialogOpen] = useState(false);
	const hideDialog = () => setDialogOpen(false);
	const showDialog = () => setDialogOpen(true);

	const app = useContext(AppContext);

	const { lawsuitId, name, clientFormId } = route.params;

	const styles = StyleSheet.create({
		contentView: {
			flex: 1,
			justifyContent: 'flex-start',
			padding: 25
		},
		submitButton: {
			justifyContent: 'center',
			height: 55,
			marginTop: 25,
			marginBottom: 25,
			display: elementVisibility ? null : 'none',
			flex: 1
		},
		submitButtonText: {
			fontSize: 18,
			padding: 5
		},
		backButton: {
			justifyContent: 'center',
			height: 55,
			marginTop: 25,
			marginBottom: 25,
			display: elementVisibility ? null : 'none',
			flex: 1
		},
		backButtonText: {
			fontSize: 18,
			padding: 5
		},
		titleTextWeb: {
			textAlign: 'center',
			display: elementVisibility ? null : 'none'
		},
		divider: {
			marginTop: 15
		},
		formTopBar: {
			flexDirection: 'row',
			justifyContent: 'space-between',
			display: elementVisibility ? null : 'none'
		},
		buttonContainer: {
			flexDirection: 'row',
			width: '100%',
			gap: 16
		},
		dialogTitle: {
			fontSize: 20,
			fontWeight: '700',
			marginTop: 0,
			marginBottom: 28,
			marginRight: 0,
			marginLeft: 0
		},
		dialogBody: {
			fontSize: 16,
			fontWeight: '400',
			gap: 16,
			flexDirection: 'column',
			paddingBottom: 28
		},
		dialogContainer: {
			padding: 30,
			width: 612,
			borderRadius: 12,
			alignSelf: 'center'
		},
		dialogBold: {
			fontWeight: '700'
		},
		dialogButtonContainer: {
			flexDirection: 'row',
			gap: 16,
			paddingBottom: 0,
			paddingTop: 0,
			paddingLeft: 0,
			paddingRight: 0
		},
		dialogContent: {
			paddingBottom: 0,
			paddingRight: 0,
			paddingLeft: 0
		},
		dialogSubmitText: {
			fontSize: 18,
			fontWeight: '700',
			marginTop: 22,
			marginBottom: 22,
			marginRight: 11,
			marginLeft: 11,
			textTransform: 'none'
		},
		dialogBackText: {
			fontSize: 18,
			fontWeight: '700',
			marginTop: 22,
			marginBottom: 22,
			marginRight: 11,
			marginLeft: 11,
			textTransform: 'none'
		}
	});

	const handleBack = () => {
		if (form?.Submitted) {
			const parms = { lawsuitId, name };
			navigation.replace('Case', parms);
		} else {
			const parms = { lawsuitId, name, clientFormId };
			navigation.replace('Form', parms);
		}
	};
	const backButton =<HeaderBackButton onPress={handleBack} />;

	useLayoutEffect(() => {
		navigation.setOptions({
			headerLeft: () => backButton,
			headerTitle: form?.ClientForm?.Name ?? ''
		});
	}, [form]);

	useEffect(() => {
		const loadData = async () => {
			const response = await V1Request(
				'get',
				`ReviewForm/${lawsuitId}?form=${clientFormId}`,
				null,
				null,
				'json',
				app.setLoading
			);

			if (response.status === 401 || response.status === 403) {
				LoginRedirect(app, navigation);
				return;
			}

			setQuestions(response.data.questions);
			setForm(response.data.form);
			if (response.data.form.Submitted) setButtonText('Back to Forms');
			else setButtonText('Submit Form');
			setElementVisibility(true);
		};
		loadData();
	}, []);

	const handleButton = async () => {
		if (form.Submitted) {
			navigation.replace('Case', {
				lawsuitId,
				name
			});
			return;
		} else {
			submitForm();
		}
	};

	const submitForm = async () => {
		const response = await V1Request(
			'post',
			`ReviewForm/${lawsuitId}`,
			null,
			{ form: clientFormId },
			'json',
			app.setLoading
		);
		if (response.status === 200) {
			navigation.replace('Case', {
				lawsuitId,
				name,
				submittedFormTitle: form?.ClientForm?.Name,
			});
		} else {
			// Alert Something wrong in save form
			app.setNotificationTitle('Oops, something went wrong.');
			app.setNotificationMsg('Please try again later');
			app.setNotificationVisible(true);
		}
	};

	const submitTrigger =
		<View>
			<Button
				mode="contained"
				onPress={showDialog}
				style={styles.submitButton}
				labelStyle={styles.submitButtonText}
				uppercase={false}
			>
				All information is correct
			</Button>
			<Portal>
				<Dialog visible={dialogOpen} onDismiss={hideDialog} style={styles.dialogContainer}>
					<Dialog.Title style={styles.dialogTitle}>Ready to submit?</Dialog.Title>
					<Dialog.Content style={styles.dialogContent}>
						<View style={styles.dialogBody}>
							<Text>
								Please review your answers before submitting
							</Text>
							<Text>
								<Text style={styles.dialogBold}>Note</Text>
								: Changes will not be available after submission
							</Text>
						</View>
					</Dialog.Content>
					<Dialog.Actions style={styles.dialogButtonContainer}>
						<Button
							mode='outlined'
							labelStyle={styles.dialogBackText}
							uppercase={false}
							onPress={handleBack}
						>
							Continue Editing
						</Button>
						<Button
							mode='contained'
							labelStyle={styles.dialogSubmitText}
							uppercase={false}
							onPress={submitForm}
						>
							Submit Form
						</Button>
					</Dialog.Actions>
				</Dialog>
			</Portal>
		</View>;
	return <>{Platform.OS === 'web' ? WebView() : MobileView()}</>;

	function WebView() {
		return (
			<BrandBackground>
				<ScrollView>
					<WebContainer size={'md'}>
						<View style={styles.formTopBar}>
							{backButton}
							<Title style={styles.titleTextWeb}>
								{form?.ClientForm?.Name}
							</Title>
							<View style={styles.titleTextWeb}></View>
						</View>
						<Divider style={styles.divider} />
						<View style={styles.contentView}>
							{questions?.filter(ShowQuestions).map((q) => {
								return <Review key={q.Id} question={q} />;
							})}
							<View style={styles.buttonContainer}>
								<Button
									onPress={handleBack}
									style={styles.backButton}
									labelStyle={styles.backButtonText}
									mode='outlined'
									uppercase={false}
								>
									Go back to form	
								</Button>
								{buttonText !== 'Back to Forms' && submitTrigger}
							</View>
						</View>
					</WebContainer>
				</ScrollView>
			</BrandBackground>
		);
	}

	function MobileView() {
		return (
			<ScrollView>
				<View style={styles.contentView}>
					{questions?.filter(ShowQuestions).map((q) => {
						return <Review key={q.Id} question={q} />;
					})}
					<Button
						mode="contained"
						onPress={handleButton}
						style={styles.submitButton}
						labelStyle={styles.submitButtonText}
						uppercase={false}
					>
						{buttonText}
					</Button>
				</View>
			</ScrollView>
		);
	}
};
